import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mail, Phone, Linkedin, Loader2, Calendar, User as UserIcon } from 'lucide-react';
import { User } from '@/api/entities';
import { format, parseISO, isValid } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OutboxDrawer from '../outbox/OutboxDrawer';
import { notify } from '@/components/ui/notify';
import { useTranslation } from '@/components/utils/translations';
import { appendAnalyticsWindow, coerceAnalyticsWindow } from '@/lib/analyticsWindow';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

const CHANNEL_ICONS = {
  email: Mail,
  call: Phone,
  linkedin: Linkedin
};

const safeFormatDay = (dateValue) => {
  if (!dateValue) return 'Unknown';
  try {
    const date = parseISO(dateValue);
    if (!isValid(date)) return 'Unknown';
    return format(date, 'EEEE, MMMM d, yyyy');
  } catch (e) {
    return 'Unknown';
  }
};

export default function HistoryTab({ tenantId, timeRange, analyticsWindow, currentUser, userId }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('all');
  const [users, setUsers] = useState([]);
  const [selectedDraftForDrawer, setSelectedDraftForDrawer] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [tenantId, timeRange, analyticsWindow, userId]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const to = new Date().toISOString();
      const from = new Date(Date.now() - (parseInt(timeRange) * 24 * 60 * 60 * 1000)).toISOString();

      const params = new URLSearchParams({
        tenant_id: tenantId,
        from,
        to
      });
      const window = coerceAnalyticsWindow(analyticsWindow ?? timeRange);
      if (window.mode === 'custom') {
        appendAnalyticsWindow(params, window);
      }
      if (userId) params.append('user_id', userId);

      const response = await fetch(
        `${API_BASE_URL}/api/analytics-v2/outreach-history?${params.toString()}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
        
        const uniqueUsers = [...new Set(
          (data.history || [])
            .map(item => item.user_email)
            .filter(email => email && email !== 'Unknown')
        )];
        setUsers(uniqueUsers.sort());
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDraft = useCallback((item) => {
    const userId = currentUser?.id || localStorage.getItem('user-id');

    if (!userId) {
      notify({
        title: t('error'),
        description: t('userIdMissing'),
        variant: 'destructive'
      });
      return;
    }

    // Debug: log history item fields
    console.log("🧪 History item fields:", Object.keys(item));
    console.log("🧪 History item data:", {
      person_email: item.person_email,
      linkedin_url: item.linkedin_url,
      phone: item.phone,
      person_id: item.person_id,
      sequence_id: item.sequence_id,
      stage_no: item.stage_no,
      step_id: item.step_id
    });

    // For history items, use the data directly - don't fetch from outbox API
    // (The outbox API only has sequence_steps, not sent_messages)
    
    const stepId = item.step_id || item.stepId || item.id;
    
    // Extract contact info from history item
    const personEmail = item.person_email || item.email;
    const personLinkedIn = item.linkedin_url || item.linkedinUrl;
    const personPhone = item.phone;
    
    // Normalize sequence_id
    const sequenceId = item.sequence_id || item.sequenceId || null;
    
    // Normalize stage
    const stageNo = item.stage_no || item.stage || item.touch_number || null;
    
    // Build person object with contact info from history item
    const personFromItem = {
      name: item.person_name,
      full_name: item.person_name,
      first_name: item.person_name?.split(' ')[0],
      last_name: item.person_name?.split(' ').slice(1).join(' '),
      email: personEmail,
      linkedin_url: personLinkedIn,
      phone: personPhone,
      person_id: item.person_id,
      id: item.person_id
    };
    
    // Build the draft object directly from history item data
    const normalizedDraft = {
      id: stepId || item.id,
      step_id: stepId,
      stepId: stepId,
      draftId: stepId || item.id,
      tenantId: tenantId,
      userId: userId,
      channel: item.channel || 'email',
      // Person fields
      person_id: item.person_id,
      personId: item.person_id,
      person: personFromItem,
      person_name: item.person_name,
      person_email: personEmail,
      email: personEmail,
      phone: personPhone,
      linkedin_url: personLinkedIn,
      linkedinUrl: personLinkedIn,
      // Account fields
      account_id: item.account_id,
      accountId: item.account_id,
      account: { 
        name: item.account_name, 
        account_name: item.account_name,
        id: item.account_id 
      },
      account_name: item.account_name,
      // Sequence fields
      sequence_id: sequenceId,
      sequenceId: sequenceId,
      sequence: sequenceId ? { id: sequenceId } : null,
      // Stage fields
      stage: stageNo,
      stage_no: stageNo,
      stageNo: stageNo,
      currentStage: stageNo,
      touch_number: stageNo,
      touchNumber: stageNo,
      // Message content
      subject: item.subject || '',
      body_html: item.body_html || item.body || '',
      bodyHtml: item.body_html || item.body || '',
      body: item.body_html || item.body || '',
      html: item.body_html || item.body || '',
      // Status - mark as history item
      status: item.status || 'sent',
      is_history: true,  // 👈 KEY: This tells OutboxDrawer not to fetch
      // Metadata
      archetype: item.archetype,
      playbook_name: item.playbook_name,
      source: item.source,
      completed_at: item.completed_at
    };

    console.log("📬 Opening history item in drawer:", {
      person_name: normalizedDraft.person_name,
      person_email: normalizedDraft.person_email,
      linkedin_url: normalizedDraft.linkedin_url,
      phone: normalizedDraft.phone,
      channel: normalizedDraft.channel,
      is_history: normalizedDraft.is_history
    });

    setSelectedDraftForDrawer(normalizedDraft);
  }, [tenantId, currentUser?.id]);

  const groupedByDate = React.useMemo(() => {
    const filtered = selectedUser === 'all' 
      ? history 
      : history.filter(item => item.user_email === selectedUser);
    
    const grouped = {};
    filtered.forEach(item => {
      const dateKey = item.completed_at ? item.completed_at.split('T')[0] : 'unknown';
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(item);
    });
    
    return Object.entries(grouped)
      .sort((a, b) => new Date(b[0]) - new Date(a[0]));
  }, [history, selectedUser]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[#0A1A2F]">{t('outreachHistory')}</h2>
          <p className="text-sm text-[#5C6B7A] mt-1">{t('outreachHistorySubtitle')}</p>
        </div>
        
        {users.length > 1 && (
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allUsers')}</SelectItem>
              {users.map(user => (
                <SelectItem key={user} value={user}>{user}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {groupedByDate.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#5C6B7A]">{t('noOutreachHistoryPeriod')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedByDate.map(([date, items]) => (
            <Card key={date} className="border-0 shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                  <Calendar className="w-4 h-4 text-[#00C2C2]" />
                  <h3 className="font-semibold text-[#0A1A2F]">{safeFormatDay(date)}</h3>
                  <Badge variant="outline" className="ml-auto">{items.length} activities</Badge>
                </div>
                
                <div className="space-y-3">
                  {items.map((item, idx) => {
                    const Icon = CHANNEL_ICONS[item.channel] || Mail;
                    const iconColor = 
                      item.channel === 'email' ? 'text-blue-600' :
                      item.channel === 'linkedin' ? 'text-blue-600' :
                      item.channel === 'call' ? 'text-green-600' :
                      'text-blue-600';
                    
                    const sourceLabel = 
                      item.source === 'playbook' ? item.playbook_name || 'Playbook' :
                      item.source === 'diy_playbook' ? item.playbook_name || 'DIY Playbook' :
                      item.source === 'profile_outreach' ? 'Profile Outreach' :
                      item.source === 'today_plan' ? "Today's Plan" :
                      item.source === 'recommendation' ? 'NEO Recommendation' :
                      'Manual';
                    
                    // Handle missing names gracefully
                    const hasPersonName = item.person_name && item.person_name !== 'Unknown';
                    const hasAccountName = item.account_name && item.account_name !== 'Unknown';
                    const displayName = hasPersonName ? item.person_name : item.subject;
                    const displaySubtitle = hasAccountName 
                      ? item.account_name 
                      : (hasPersonName ? item.subject : `${item.channel} outreach`);
                    
                    return (
                      <div 
                        key={item.id || idx} 
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => handleOpenDraft(item)}
                      >
                        <Icon className={`w-4 h-4 mt-0.5 ${iconColor}`} />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#0A1A2F] truncate">
                                {displayName}
                              </p>
                              <p className="text-xs text-[#5C6B7A] truncate">
                                {displaySubtitle}
                              </p>
                            </div>
                            {item.user_email && item.user_email !== 'Unknown' && (
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <UserIcon className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-[#5C6B7A]">
                                  {item.user_email.split('@')[0]}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {sourceLabel}
                            </Badge>
                            <Badge className="text-xs bg-green-100 text-green-800">
                              {item.channel}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedDraftForDrawer && (
        <OutboxDrawer
          draft={selectedDraftForDrawer}
          tenantId={tenantId}
          userId={currentUser?.id || localStorage.getItem('user-id')}
          onClose={() => {
            setSelectedDraftForDrawer(null);
          }}
          onSent={() => {
            setSelectedDraftForDrawer(null);
          }}
        />
      )}
    </div>
  );
}